'use client';

import React from 'react';
import { IResortShortReport } from '@/api/region/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { DataTable, DataTableSort } from '@/components/common/DataTable';
import { cn } from '@/util/cn';
import { MobileDataCard } from '@/components/common/MobileDataCard';
import { ResortNameLink } from '@/components/common/ResortNameLink';
import { SectionCard } from '@/components/ui/section-card';
import { formatDateFromStr, SHORT_DATE_FORMAT } from '@/util/formatDate';

const SORT_KEY = {
  title: 'title',
  date: 'date',
} as const;

// 2-col table (resort name + projected date). The link target differs
// by page (skireport → snow report; open-resorts / projected-openings →
// trip planning overview), so callers pass `linkTo`. The column header
// text also differs, so callers pass `dateLabel`. The date defaults to
// the projected opening date; the closing-dates page passes `getDate`
// for the closing date and a `badge` for its inline "closed" tag.
//
// Desktop headers are click-to-sort (the date column starts ascending —
// soonest date first). Sorting is client-side, which only tells the truth
// once every row is present: on a region spanning more than one page the
// caller passes `onSortIntent` to pull the remainder first, and `isLoading`
// while that runs. The mobile stack keeps the server order.
export const ProjectedOpeningTable = ({
  rows, linkTo, dateLabel, showRegion = false,
  getDate = (r) => r.status?.openingDate,
  badge, onSortIntent, isLoading = false,
}: {
  rows: IResortShortReport[];
  linkTo: (r: IResortShortReport) => string;
  dateLabel: string;
  /** Append ", Region" to each resort name (see util/spansMultipleRegions). */
  showRegion?: boolean;
  /** Raw date string backing the date column (default: projected opening date). */
  getDate?: (r: IResortShortReport) => string | undefined;
  /** Optional inline tag rendered after the resort name (e.g. a "closed" badge). */
  badge?: (r: IResortShortReport) => React.ReactNode;
  /** Fired on a sort header click, so the caller can complete the set first. */
  onSortIntent?: () => void;
  /** Rows are still arriving; the order on screen is provisional. */
  isLoading?: boolean;
}) => {
  const { t } = useOTSTranslation('common');
  const dash = t('common.dash');
  const dateFor = (r: IResortShortReport) => {
    const d = getDate(r);
    return d ? formatDateFromStr(d, SHORT_DATE_FORMAT) : dash;
  };

  const [sort, setSort] = React.useState<DataTableSort | null>(null);
  const handleSortChange = React.useCallback((next: DataTableSort) => {
    setSort(next);
    onSortIntent?.();
  }, [onSortIntent]);
  const sortedRows = React.useMemo(() => {
    if (!sort) return rows;
    // Dates arrive as plain yyyy-MM-dd strings; unparseable/missing ones
    // render a dash and group at the bottom in either direction.
    const timeFor = (r: IResortShortReport): number | undefined => {
      const d = getDate(r);
      const time = d ? Date.parse(d) : NaN;
      return Number.isNaN(time) ? undefined : time;
    };
    const dirMul = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort.key === SORT_KEY.title) return a.title.localeCompare(b.title) * dirMul;
      const av = timeFor(a);
      const bv = timeFor(b);
      if (av === undefined && bv === undefined) return 0;
      if (av === undefined) return 1;
      if (bv === undefined) return -1;
      return (av - bv) * dirMul;
    });
  }, [rows, sort, getDate]);

  return (
    <>
      <div
        className={cn('hidden md:block transition-opacity', isLoading && 'opacity-60')}
        aria-busy={isLoading || undefined}
      >
        <DataTable sort={sort} onSortChange={handleSortChange}>
          <DataTable.Head>
            <DataTable.HeadRow>
              <DataTable.HeadCell sortKey={SORT_KEY.title}>
                {t('region.liftTickets.resort')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right" sortKey={SORT_KEY.date}>
                {dateLabel}
              </DataTable.HeadCell>
            </DataTable.HeadRow>
          </DataTable.Head>
          <DataTable.Body>
            {sortedRows.map((r, i) => (
              <DataTable.Row key={r.uuid} zebra={i % 2 === 0}>
                <DataTable.RowHeader>
                  <ResortNameLink resort={r} href={linkTo(r)} showRegion={showRegion} />
                  {badge?.(r)}
                </DataTable.RowHeader>
                <DataTable.Cell align="right">{dateFor(r)}</DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable>
      </div>

      <div className="md:hidden">
        <SectionCard as="div" padding="none" className="overflow-hidden">
          {rows.map((r) => (
            <MobileDataCard
              key={r.uuid}
              heading={(
                <>
                  <ResortNameLink resort={r} href={linkTo(r)} showRegion={showRegion} />
                  {badge?.(r)}
                </>
              )}
            >
              <MobileDataCard.Stat label={dateLabel} value={dateFor(r)} />
            </MobileDataCard>
          ))}
        </SectionCard>
      </div>
    </>
  );
};
